import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "@/infra/locale";
import { LOCALE_GET_PARAM } from "@/infra/router/utils";
import { type AppLocale, loggerService } from "@akina/infra";
import { useLocaleStore, useScreenSizeDetector, Link, Text, Headline, Icon } from "@akina/presentation";
import { isApp } from "@akina/utils";

function useAttemptToOpenDesktopApp() {
  const [deepLinkUrl, setDeepLinkUrl] = useState<string>("");
  const [parsedPathUrl, setParsedPathUrl] = useState<string>("");
  useEffect(() => {
    if (isApp) return;
    const checkIfDesktopAppIsRunning = async () => {
      try {
        const { pathname, search } = location;
        const parsedPath = pathname.replace(/^\/app/, "");
        const deepLinkUrl = `akina-care://${parsedPath.slice(1)}${search}`;
        setParsedPathUrl(parsedPath);
        setDeepLinkUrl(deepLinkUrl);
        window.location.href = deepLinkUrl;
      } catch (error) {
        loggerService.error("Error starting desktop app:", error);
      }
    };
    checkIfDesktopAppIsRunning();
  }, []);

  return { deepLinkUrl, parsedPathUrl };
}

function WebAppLink({
  url,
  text,
  urlText,
  queryParams,
  external = false,
}: Readonly<{ url: string; text: string; urlText: string; queryParams: string; external?: boolean }>) {
  return (
    <Text size="xl" fontWeight="font-medium">
      <span>{text}</span>{" "}
      <Link to={`${url}${queryParams}`} target={external ? "_blank" : undefined}>
        {urlText}
      </Link>
    </Text>
  );
}

function RedirectLinks() {
  const { formatMessage } = useIntl();
  const { deepLinkUrl, parsedPathUrl } = useAttemptToOpenDesktopApp();

  const [isLoading, setIsLoading] = useState(true);
  const setStoreLocale = useLocaleStore(state => state.setLocale);
  const [searchParams] = useSearchParams();
  const urlLocale = searchParams.get(LOCALE_GET_PARAM);

  // set the locale from the url just for the bridge page, and then reset it on page leave
  useEffect(() => {
    if (urlLocale) setStoreLocale(urlLocale as AppLocale);
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    return () => {
      clearTimeout(timeout);
    };
  }, [urlLocale, setStoreLocale]);

  const downloadUrl = formatMessage({ id: "shared.downloadDesktopAppUrl" });

  return (
    <>
      <Headline size="3xl" fontWeight="font-bold" className="leading-[40px] tracking-[-0.9px]">
        {formatMessage({
          id: "onboarding.redirectingToApp.redirectingTitle",
        })}
      </Headline>

      {isLoading ? (
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      ) : (
        <>
          <WebAppLink
            aria-describedby="want-to-try-again"
            url={deepLinkUrl}
            text={formatMessage({
              id: "onboarding.redirectingToApp.tryAgain",
            })}
            urlText={formatMessage({
              id: "onboarding.redirectingToApp.clickHere",
            })}
            queryParams=""
          />
          <WebAppLink
            aria-describedby="dont-have-the-app-yet"
            external
            url={downloadUrl}
            text={formatMessage({
              id: "onboarding.redirectingToApp.noAppYet",
            })}
            urlText={formatMessage({
              id: "onboarding.redirectingToApp.downloadHere",
            })}
            queryParams=""
          />
          <WebAppLink
            aria-describedby="continue-on-desktop"
            url={parsedPathUrl}
            text={formatMessage({
              id: "onboarding.redirectingToApp.continueOnWeb",
            })}
            urlText={formatMessage({
              id: "onboarding.redirectingToApp.continueOnWebUrl",
            })}
            queryParams={location.search}
          />
        </>
      )}
    </>
  );
}

function ContinueOnDesktopWarning() {
  const { formatMessage } = useIntl();
  return (
    <Text size="2xl" className="px-10 text-center leading-[32px]" fontWeight="font-bold">
      {formatMessage({
        id: "onboarding.redirectingToApp.openOnLaptop",
      })}
    </Text>
  );
}

function BridgeToDesktopPage() {
  const { isMobile } = useScreenSizeDetector();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6">
      <Icon type="logoSquare" className="mb-3 h-[200px] w-[200px]" />

      {isMobile ? <ContinueOnDesktopWarning /> : <RedirectLinks />}
    </div>
  );
}

export { BridgeToDesktopPage };
